import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CreditCard, Building2, Banknote, CircleDollarSign,
  Phone, Mail, MapPin, Clock, CheckCircle, XCircle, FileText, Copy, Check, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* СБП (Система Быстрых Платежей) official-style icon */
const SbpIcon = ({ style, ...props }) => (
  <svg viewBox="0 0 24 24" fill="none" style={{ width: 15, height: 15, ...style }} {...props}>
    <path d="M5.5 7.5L12 4L18.5 7.5" stroke="#F26126" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 12L12 8.5L18.5 12" stroke="#1D1346" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 16.5L12 13L18.5 16.5" stroke="#5B57A2" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5.5 16.5L12 20L18.5 16.5" stroke="#21A038" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const METHOD_ICONS = {
  card: CreditCard,
  bank: Building2,
  sbp: SbpIcon,
  cash: Banknote,
  other: CircleDollarSign,
};

const METHOD_COLORS = {
  card: { bg: '#fef3c7', icon: '#d97706', border: '#fde68a' },
  bank: { bg: '#dbeafe', icon: '#2563eb', border: '#bfdbfe' },
  sbp: { bg: '#ede9fe', icon: '#7c3aed', border: '#ddd6fe' },
  cash: { bg: '#d1fae5', icon: '#059669', border: '#a7f3d0' },
  other: { bg: '#fee2e2', icon: '#dc2626', border: '#fecaca' },
};

function formatCurrencyPublic(amount, currency = 'RUB') {
  const configs = {
    RUB: { locale: 'ru-RU', currency: 'RUB' },
    USD: { locale: 'en-US', currency: 'USD' },
    EUR: { locale: 'de-DE', currency: 'EUR' },
  };
  const cfg = configs[currency] || configs.RUB;
  return new Intl.NumberFormat(cfg.locale, {
    style: 'currency',
    currency: cfg.currency,
    minimumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDatePublic(dateString) {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateString;
  }
}

export default function PublicInvoicePage() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedMethod, setCopiedMethod] = useState(null);

  useEffect(() => {
    const html = document.documentElement;
    const hadDark = html.classList.contains('dark');
    html.classList.remove('dark');
    html.style.colorScheme = 'light';
    return () => {
      if (hadDark) html.classList.add('dark');
      html.style.colorScheme = '';
    };
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_URL}/api/invoices/public/${publicId}`);
        if (!res.ok) throw new Error('Счёт не найден');
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [publicId]);

  const copyText = async (text, methodId) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedMethod(methodId);
    setTimeout(() => setCopiedMethod(null), 2000);
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div style={S.page}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#ea580c', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ color: '#6b7280', fontSize: 14 }}>Загрузка счёта...</span>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  /* ---------- Error ---------- */
  if (error || !data) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, maxWidth: 400, padding: 48, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <FileText style={{ height: 28, width: 28, color: '#ef4444' }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Счёт не найден</h2>
          <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>Ссылка недействительна или счёт был удалён</p>
        </div>
      </div>
    );
  }

  const { invoice, user_info } = data;
  const ps = invoice.payment_settings_snapshot || {};
  const activeMethods = (ps.payment_methods || []).filter(m => m.enabled);
  const currency = user_info?.currency || 'RUB';
  const isPaid = invoice.status === 'paid';
  const isCancelled = invoice.status === 'cancelled';

  return (
    <div style={{ minHeight: '100vh', background: '#e8eaed', colorScheme: 'light', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: 440, margin: '0 auto', padding: '28px 16px 24px' }}>

        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          {(ps.company_name || user_info?.company_name || user_info?.name) && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #ea580c, #f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14 }}>
                {(ps.company_name || user_info?.company_name || user_info?.name || '').charAt(0).toUpperCase()}
              </div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>
                {ps.company_name || user_info?.company_name || user_info?.name}
              </h2>
            </div>
          )}
          <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Счёт на оплату №{invoice.invoice_number}</p>
        </div>

        {/* ── Main Card ── */}
        <div style={S.card}>

          {/* Status Banner */}
          {isPaid && (
            <div style={{ background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <CheckCircle style={{ height: 17, width: 17 }} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Оплачено</span>
            </div>
          )}
          {isCancelled && (
            <div style={{ background: 'linear-gradient(135deg, #dc2626, #ef4444)', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <XCircle style={{ height: 17, width: 17 }} />
              <span style={{ fontWeight: 600, fontSize: 14 }}>Отменён</span>
            </div>
          )}

          <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* ── Amount Block ── */}
            <div style={{
              textAlign: 'center', padding: '22px 16px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: 14, border: '2px solid #e2e8f0',
              position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', top: -25, right: -25, width: 80, height: 80, borderRadius: '50%', background: 'rgba(234,88,12,0.05)' }} />
              <div style={{ position: 'absolute', bottom: -15, left: -15, width: 55, height: 55, borderRadius: '50%', background: 'rgba(234,88,12,0.04)' }} />
              <p style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, margin: '0 0 8px', position: 'relative' }}>
                Сумма к оплате
              </p>
              <p style={{ fontSize: 36, fontWeight: 800, color: '#0f172a', margin: 0, lineHeight: 1.1, position: 'relative' }}>
                {formatCurrencyPublic(invoice.amount, currency)}
              </p>
              {invoice.description && (
                <p style={{ fontSize: 13, color: '#64748b', marginTop: 10, padding: '0 8px', lineHeight: 1.5, position: 'relative' }}>{invoice.description}</p>
              )}
            </div>

            {/* ── Order Info ── */}
            {invoice.order_info && (
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                borderRadius: 12, padding: '12px 14px', border: '1.5px solid #bfdbfe',
                display: 'flex', alignItems: 'flex-start', gap: 10
              }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText style={{ height: 14, width: 14, color: '#2563eb' }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f', margin: 0 }}>
                    Заказ {invoice.order_info.order_number_display || `#${invoice.order_info.order_number}`}
                  </p>
                  {invoice.order_info.form_schema_name && (
                    <p style={{ fontSize: 12, color: '#3b82f6', margin: '2px 0 0' }}>{invoice.order_info.form_schema_name}</p>
                  )}
                  <p style={{ fontSize: 12, color: '#2563eb', fontWeight: 500, margin: '3px 0 0' }}>
                    Сумма заказа: {formatCurrencyPublic(invoice.order_info.total, currency)}
                  </p>
                </div>
              </div>
            )}

            {/* ── Payment Methods ── */}
            {invoice.status === 'new' && activeMethods.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Способы оплаты</span>
                  <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activeMethods.map((method) => {
                    const Icon = METHOD_ICONS[method.type] || CircleDollarSign;
                    const colors = METHOD_COLORS[method.type] || METHOD_COLORS.other;
                    const isSbp = method.type === 'sbp' && method.sbp_phone;
                    const sbpPhone = (method.sbp_phone || '').replace(/\D/g, '');
                    const sbpPhoneFull = sbpPhone.startsWith('7') ? sbpPhone : '7' + sbpPhone;
                    const sbpPhoneFormatted = '+' + sbpPhoneFull;

                    /* ── SBP Bank list with SVG icons ── */
                    const SBP_BANKS = [
                      { name: 'Сбербанк', scheme: 'sberbankonline://', color: '#21a038', shortName: 'Сбер',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="10" fill="#21a038"/><path d="M7 12.5L10.5 16L17 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg> },
                      { name: 'Т-Банк', scheme: 'bank100000000004://', color: '#ffdd2d', textColor: '#333', shortName: 'Т-Банк',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#ffdd2d"/><text x="12" y="17" textAnchor="middle" fontSize="15" fontWeight="900" fill="#333" fontFamily="Arial,sans-serif">T</text></svg> },
                      { name: 'Альфа-Банк', scheme: 'alfabank://', color: '#ef3124', shortName: 'Альфа',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#ef3124"/><text x="12" y="17" textAnchor="middle" fontSize="14" fontWeight="900" fill="#fff" fontFamily="Arial,sans-serif">A</text></svg> },
                      { name: 'ВТБ', scheme: 'vtb://', color: '#002882', shortName: 'ВТБ',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#002882"/><text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="900" fill="#fff" fontFamily="Arial,sans-serif">ВТБ</text></svg> },
                      { name: 'Газпромбанк', scheme: 'gpb://', color: '#0061af', shortName: 'Газпром',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#0061af"/><path d="M8 8L12 5L16 8L16 16L12 19L8 16Z" fill="none" stroke="#fff" strokeWidth="1.5"/><circle cx="12" cy="12" r="2.5" fill="#fff"/></svg> },
                      { name: 'Райффайзен', scheme: 'raiffeisen://', color: '#fee600', textColor: '#333', shortName: 'Райф',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#fee600"/><path d="M7 12H17M12 7V17" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round"/></svg> },
                      { name: 'Совкомбанк', scheme: 'sovcombank://', color: '#e31e25', shortName: 'Совком',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#e31e25"/><text x="12" y="17" textAnchor="middle" fontSize="14" fontWeight="900" fill="#fff" fontFamily="Arial,sans-serif">S</text></svg> },
                      { name: 'Росбанк', scheme: 'rosbank://', color: '#e30613', shortName: 'Росбанк',
                        icon: <svg viewBox="0 0 24 24" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" fill="#e30613"/><path d="M8 7H14C15.7 7 17 8.3 17 10C17 11.7 15.7 13 14 13H8V7ZM8 13V17" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg> },
                    ];

                    const handleBankClick = async (bank) => {
                      const textToCopy = sbpPhoneFormatted;
                      try {
                        await navigator.clipboard.writeText(textToCopy);
                      } catch (e) {
                        // fallback
                        const ta = document.createElement('textarea');
                        ta.value = textToCopy;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      setCopiedMethod(method.id + '_bank_' + bank.shortName);
                      setTimeout(() => setCopiedMethod(null), 3000);
                      // Try to open bank app
                      window.location.href = bank.scheme;
                    };

                    const handleCopyAll = async () => {
                      const text = `Перевод по СБП\nТелефон: ${sbpPhoneFormatted}\nСумма: ${formatCurrencyPublic(invoice.amount, currency)}\n${method.sbp_recipient ? 'Получатель: ' + method.sbp_recipient + '\n' : ''}${method.sbp_bank ? 'Банк: ' + method.sbp_bank + '\n' : ''}Назначение: Счёт №${invoice.invoice_number}`;
                      try {
                        await navigator.clipboard.writeText(text);
                      } catch (e) {
                        const ta = document.createElement('textarea');
                        ta.value = text;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      setCopiedMethod(method.id + '_all');
                      setTimeout(() => setCopiedMethod(null), 3000);
                    };

                    /* ── SBP with Bank buttons ── */
                    if (isSbp) {
                      return (
                        <div key={method.id} style={{
                          border: '1.5px solid #ddd6fe', borderRadius: 12, overflow: 'hidden', background: '#fff'
                        }}>
                          {/* Header */}
                          <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ height: 30, width: 30, borderRadius: 8, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <SbpIcon style={{ height: 18, width: 18 }} />
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                              {method.name || 'Оплата через СБП'}
                            </span>
                          </div>

                          <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>

                            {/* Recipient info */}
                            <div style={{
                              width: '100%', background: '#faf5ff', borderRadius: 10, padding: '10px 12px',
                              display: 'flex', flexDirection: 'column', gap: 4
                            }}>
                              {method.sbp_recipient && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span style={{ color: '#6b7280' }}>Получатель</span>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{method.sbp_recipient}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                <span style={{ color: '#6b7280' }}>Телефон</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{method.sbp_phone}</span>
                                  <button
                                    onClick={() => copyText(method.sbp_phone, method.id + '_phone')}
                                    style={{
                                      border: 'none', background: 'none', cursor: 'pointer', padding: 2,
                                      color: copiedMethod === method.id + '_phone' ? '#059669' : '#9ca3af'
                                    }}
                                  >
                                    {copiedMethod === method.id + '_phone' 
                                      ? <Check style={{ height: 12, width: 12 }} />
                                      : <Copy style={{ height: 12, width: 12 }} />}
                                  </button>
                                </div>
                              </div>
                              {method.sbp_bank && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span style={{ color: '#6b7280' }}>Банк</span>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{method.sbp_bank}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                <span style={{ color: '#6b7280' }}>Сумма</span>
                                <span style={{ color: '#7c3aed', fontWeight: 700 }}>{formatCurrencyPublic(invoice.amount, currency)}</span>
                              </div>
                            </div>

                            {/* Bank buttons grid */}
                            <div style={{ width: '100%' }}>
                              <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 8px 0', textAlign: 'center', fontWeight: 500 }}>
                                Выберите ваш банк для перевода:
                              </p>
                              <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6
                              }}>
                                {SBP_BANKS.map((bank) => {
                                  const isCopied = copiedMethod === method.id + '_bank_' + bank.shortName;
                                  return (
                                    <button
                                      key={bank.shortName}
                                      onClick={() => handleBankClick(bank)}
                                      style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        gap: 3, padding: '8px 4px', borderRadius: 8,
                                        border: isCopied ? '1.5px solid #059669' : '1.5px solid #e5e7eb',
                                        background: isCopied ? '#ecfdf5' : '#fff',
                                        cursor: 'pointer', fontSize: 10, fontWeight: 500,
                                        color: isCopied ? '#059669' : '#374151',
                                        transition: 'all 0.15s ease',
                                        minHeight: 52
                                      }}
                                    >
                                      <div style={{
                                        width: 28, height: 28, borderRadius: 7, overflow: 'hidden',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                      }}>
                                        {bank.icon}
                                      </div>
                                      <span style={{ lineHeight: 1.1, textAlign: 'center' }}>
                                        {isCopied ? 'Открываю...' : bank.shortName}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                              <p style={{ fontSize: 10, color: '#9ca3af', margin: '6px 0 0 0', textAlign: 'center' }}>
                                При нажатии номер скопируется и откроется приложение банка
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    /* ── Card transfer with structured fields ── */
                    const isCard = method.type === 'card' && method.card_number;
                    if (isCard) {
                      return (
                        <div key={method.id} style={{
                          border: `1.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', background: '#fff'
                        }}>
                          {/* Header */}
                          <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ height: 30, width: 30, borderRadius: 8, background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icon style={{ height: 15, width: 15, color: colors.icon }} />
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                              {method.name || 'Перевод по номеру карты'}
                            </span>
                          </div>

                          <div style={{ padding: '0 14px 14px' }}>
                            <div style={{
                              width: '100%', background: '#fffbeb', borderRadius: 10, padding: '10px 12px',
                              display: 'flex', flexDirection: 'column', gap: 4
                            }}>
                              {method.card_bank && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span style={{ color: '#6b7280' }}>Банк</span>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{method.card_bank}</span>
                                </div>
                              )}
                              {method.card_recipient && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span style={{ color: '#6b7280' }}>Получатель</span>
                                  <span style={{ color: '#111827', fontWeight: 500 }}>{method.card_recipient}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                                <span style={{ color: '#6b7280' }}>Номер карты</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: '#111827', fontWeight: 600, fontFamily: '"SF Mono", "Fira Code", monospace', letterSpacing: '0.05em' }}>{method.card_number}</span>
                                  <button
                                    onClick={() => copyText(method.card_number, method.id + '_card')}
                                    style={{
                                      border: 'none', background: 'none', cursor: 'pointer', padding: 2,
                                      color: copiedMethod === method.id + '_card' ? '#059669' : '#9ca3af'
                                    }}
                                  >
                                    {copiedMethod === method.id + '_card'
                                      ? <Check style={{ height: 12, width: 12 }} />
                                      : <Copy style={{ height: 12, width: 12 }} />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    /* ── Regular methods (bank, cash, etc.) ── */
                    return (
                      <div key={method.id} style={{
                        border: `1.5px solid ${colors.border}`,
                        borderRadius: 12, padding: 12, background: '#fff',
                        transition: 'box-shadow 0.15s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: method.details ? 8 : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{
                              height: 30, width: 30, borderRadius: 8, background: colors.bg,
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <Icon style={{ height: 15, width: 15, color: colors.icon }} />
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                              {method.name || {card: 'Перевод по номеру карты', bank: 'По реквизитам', sbp: 'Оплата через СБП', cash: 'Наличные', other: 'Другое'}[method.type] || method.type}
                            </span>
                          </div>
                          {method.details && (
                            <button
                              onClick={() => copyText(method.details, method.id)}
                              style={{
                                height: 30, padding: '0 10px', fontSize: 11, fontWeight: 500,
                                border: copiedMethod === method.id ? '1.5px solid #a7f3d0' : '1.5px solid #e5e7eb',
                                borderRadius: 7,
                                background: copiedMethod === method.id ? '#ecfdf5' : '#fafafa',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                color: copiedMethod === method.id ? '#059669' : '#4b5563',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              {copiedMethod === method.id ? (
                                <><Check style={{ height: 11, width: 11 }} /> Скопировано</>
                              ) : (
                                <><Copy style={{ height: 11, width: 11 }} /> Копировать</>
                              )}
                            </button>
                          )}
                        </div>
                        {method.details && (
                          <pre style={{
                            fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', fontFamily: '"SF Mono", "Fira Code", monospace',
                            background: '#f8fafc', borderRadius: 8, padding: '10px 12px', margin: 0,
                            border: '1px solid #f1f5f9', lineHeight: 1.5
                          }}>
                            {method.details}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No payment methods */}
            {invoice.status === 'new' && activeMethods.length === 0 && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Оплата</span>
                  <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                  Свяжитесь для уточнения способа оплаты
                </p>
              </div>
            )}

            {/* ── Contact Info ── */}
            {(ps.company_name || ps.company_inn || ps.company_phone || ps.company_email) && (
              <div style={{ background: '#f9fafb', borderRadius: 12, padding: '14px 16px', border: '1px solid #f3f4f6' }}>
                <p style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>
                  Контактная информация
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#374151' }}>
                  {ps.company_name && <p style={{ fontWeight: 600, margin: 0, color: '#111827' }}>{ps.company_name}</p>}
                  {ps.company_inn && <p style={{ margin: 0, color: '#6b7280' }}>ИНН: {ps.company_inn}</p>}
                  {ps.company_address && (
                    <p style={{ display: 'flex', alignItems: 'center', gap: 5, margin: 0, color: '#6b7280' }}>
                      <MapPin style={{ height: 12, width: 12, flexShrink: 0, color: '#9ca3af' }} /> {ps.company_address}
                    </p>
                  )}
                  {ps.company_phone && (
                    <a href={`tel:${ps.company_phone}`} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#ea580c', textDecoration: 'none', fontWeight: 500 }}>
                      <Phone style={{ height: 12, width: 12, flexShrink: 0 }} /> {ps.company_phone}
                    </a>
                  )}
                  {ps.company_email && (
                    <a href={`mailto:${ps.company_email}`} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#ea580c', textDecoration: 'none', fontWeight: 500 }}>
                      <Mail style={{ height: 12, width: 12, flexShrink: 0 }} /> {ps.company_email}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Created date */}
            <p style={{ fontSize: 11, color: '#b0b6bf', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, margin: 0 }}>
              <Clock style={{ height: 11, width: 11 }} />
              Счёт от {formatDatePublic(invoice.created_at)}
            </p>
          </div>
        </div>

        {/* ── Footer ── */}
        <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 20 }}>
          Создано в{' '}
          <a href="https://yadela.ru" target="_blank" rel="noopener noreferrer"
            style={{ color: '#ea580c', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            YaDela <ExternalLink style={{ height: 10, width: 10 }} />
          </a>
        </p>
      </div>
    </div>
  );
}

/* ── Shared Styles ── */
const S = {
  page: {
    minHeight: '100vh',
    background: '#e8eaed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    colorScheme: 'light',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    background: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.12), 0 4px 12px -4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
};
